import { useMemo } from 'react';

/**
 * Hook for checking user permissions against module access levels.
 *
 * If no currentUser is passed, auto-reads from localStorage.
 *
 * Usage:
 *   const { canEdit, canView, canAccessModule } = usePermissions();
 *   if (canEdit('order_processing')) { ... }
 */
export const usePermissions = (currentUser) => {
  // Auto-read from localStorage if no user provided
  const user = useMemo(() => {
    if (currentUser) return currentUser;
    try {
      const stored = localStorage.getItem('currentUser');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [currentUser]);

  return useMemo(() => {
    const isSuperAdmin = user?.isSuperAdmin === true ||
      (typeof user?.organization === 'string' && user.organization.toUpperCase() === 'BOTH');

    // Get permissions from profile (structured object) or top-level (backward compat)
    const perms = user?.profile?.permissions || user?.permissions;
    const hasStructuredPerms = perms && !Array.isArray(perms) && typeof perms === 'object' && perms.modules;

    /**
     * Check if user can access (see) a module in navigation
     */
    const canAccessModule = (moduleKey) => {
      if (isSuperAdmin) return true;
      if (!hasStructuredPerms) return true;
      const mod = perms.modules[moduleKey];
      if (!mod) return true;
      return mod.visible === true;
    };

    /**
     * Get the access level for a module: 'none', 'view', or 'edit'
     */
    const getAccessLevel = (moduleKey) => {
      if (isSuperAdmin) return 'edit';
      if (!hasStructuredPerms) return 'edit';
      const mod = perms.modules[moduleKey];
      if (!mod) return 'edit';
      return mod.access || 'edit';
    };

    /**
     * Check if user has edit access to a module
     */
    const canEdit = (moduleKey) => getAccessLevel(moduleKey) === 'edit';

    /**
     * Check if user has at least view access to a module
     */
    const canView = (moduleKey) => ['view', 'edit'].includes(getAccessLevel(moduleKey));

    return { canAccessModule, getAccessLevel, canEdit, canView, isSuperAdmin };
  }, [user]);
};
